import { useCallback, useMemo, useState } from "react";
import { useGestaoTreinamentosCadastroPagination } from "../hooks/GestaoTreinamentos_cadastro/pagination";
import { FuncionarioTreinamento } from "../../domain/models/funcionario_treinamento";
import { processError } from "../../../../common/utils/base_error";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useLocation } from "react-router";
import { GridRowSelectionModel } from "@mui/x-data-grid-pro";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { TreinamentoForm } from "../schemas/GestaoTreinamentos_cadastro/treinamento_schema";
import { useGestaoTreinamentosCadastroFilters } from "../hooks/GestaoTreinamentos_cadastro/filters";
import { AvaliarTreinamentoForm } from "../schemas/GestaoTreinamentos_cadastro/avaliar_treinamento_schema";
import { RelatorioFuncionarioTreinamentoForm } from "../schemas/GestaoTreinamentos_cadastro/relatorio_funcionario_treinamento_schema";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";

import CommonConstants from "../../../../common/constants";
import { Funcionario } from "../../domain/models/funcionario";
import dayjs from "dayjs";
import { Afastamento } from "../../domain/models/afastamento";

/**
 * useGestaoTreinamentosCadastroViewModel
 */

export const useGestaoTreinamentosCadastroViewModel = () => {
  const {
    reports: {
      getReportFuncionarioTreinamentoUseCase,
    },
    rh: {
      getAfastamentosUseCase,
      getFuncionarioByIdUseCase,
      getFuncionarioTreinamentosUseCase,
      getTreinamentosDropDownUseCase,
      getFuncionarioAssinaturaDropDownUseCase,
      createFuncionarioTreinamentoUseCase,
      updateFuncionarioTreinamentoUseCase,
      deleteFuncionarioTreinamentoUseCase,
      deleteFuncionarioTreinamentosLoteUseCase,
      avaliarTreinamentoUseCase,
    },
  } = useDependencyInjection();

  const location = useLocation();

  const backOrNavigate = useBackOrNavigate();

  const { pushErrorNotification, pushInfoNotification } = usePageLoader();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [funcionario, setFuncionario] = useState<Funcionario | null>();
  const [afastamentos, setAfastamentos] = useState<Afastamento[]>();

  const [openCadastroFuncionarioTreinamentoModal, setOpenCadastroFuncionarioTreinamentoModal] = useState(false);
  const [openAvaliarFuncionarioTreinamentoModal, setOpenAvaliarFuncionarioTreinamentoModal] = useState(false);
  const [openDeletarFuncionarioTreinamentoModal, setOpenDeletarFuncionarioTreinamentoModal] = useState(false);
  const [openDeletarFuncionarioTreinamentoLoteModal, setOpenDeletarFuncionarioTreinamentoLoteModal] = useState(false);
  const [openRelatorioFuncionarioTreinamentoModal, setOpenRelatorioFuncionarioTreinamentoModal] = useState(false);

  const [idFuncionario, setIdFuncionario] = useState<number>();
  const [funcionarioTreinamentos, setFuncionarioTreinamentos] = useState<FuncionarioTreinamento[]>();
  const [selectedFuncionarioTreinamento, setSelectedFuncionarioTreinamento] = useState<FuncionarioTreinamento>();

  const [rowsSelectionModel, setRowsSelectionModel] = useState<GridRowSelectionModel>();

  const filters = useGestaoTreinamentosCadastroFilters();
  const pagination = useGestaoTreinamentosCadastroPagination();

  const [reportViewerState, startReportViewerProcess] = useReportViewer();

  const status = useMemo(() => {
    const startToday = dayjs().startOf('day').utc().toDate();
    const endToday = dayjs().endOf('day').utc().toDate();

    if (funcionario === undefined || funcionario === null) return null;
    if (funcionario.dataDemissao) return 0; // DEMITIDO
    if (funcionario.inicioAvisoPrevio) {
      if (funcionario.fimAvisoPrevio) {
        if (dayjs(funcionario.fimAvisoPrevio).isAfter(startToday)) {
          return 2; // AVISO PRÉVIO ENCERRADO
        }
      }
      return 3; // AVISO PRÉVIO
    }
    if (afastamentos) {
      const afastamentoAtivo = afastamentos.find(a => {
        if (a.dataInicio && a.dataTermino) {
          return (dayjs(a.dataInicio).isBefore(startToday) && dayjs(a.dataTermino).isAfter(endToday));
        }
        if (a.dataInicio) {
          return dayjs(a.dataInicio).isBefore(startToday);
        }
        return false;
      });

      if (afastamentoAtivo) {
        return 1; // AFASTADO
      }
    }
    return 4; // ATIVO
  }, [
    funcionario,
    afastamentos,
  ]);

  /**
   * Funcoes de carregamento
   */

  const loadFuncionario = useCallback(async (params?: { idFuncionario?: number }) => {
    try {
      const id = params?.idFuncionario ?? idFuncionario;

      if (!id) throw new Error("Falha ao carregar funcionario!");

      const funcionario = await getFuncionarioByIdUseCase.getFuncionarioById(id);

      if (!funcionario) throw new Error("Falha ao carregar funcionario!");

      setFuncionario({ ...funcionario });
    } catch (e: unknown) {
      setFuncionario(null);
      throw e;
    }
  }, [
    idFuncionario,
    getFuncionarioByIdUseCase,
  ]);

  const loadAfastamentos = useCallback(async (params?: { id?: number }) => {
    try {
      const id = params?.id ?? idFuncionario;

      if (id) {
        await getAfastamentosUseCase.getAfastamentos(id, 1, 1000);
        setAfastamentos(getAfastamentosUseCase.items);
      }
    } catch (e: unknown) {
      setAfastamentos([]);
      throw e;
    }
  }, [
    idFuncionario,
    getAfastamentosUseCase,
  ]);

  const loadFuncionarioTreinamentos = useCallback(async (params?: { id?: number, page?: number, rowsPerPage?: number }) => {
    try {
      const id = params?.id ?? idFuncionario;

      if (id) {
        const page = params?.page ?? pagination.page;
        const rowsPerPage = params?.rowsPerPage ?? pagination.rowsPerPage;

        await getFuncionarioTreinamentosUseCase.getFuncionarioTreinamentos(
          id,
          page,
          rowsPerPage,
        );

        if (params && params.page !== undefined) pagination.setPage(params.page);
        if (params && params.rowsPerPage !== undefined) pagination.setRowsPerPage(params.rowsPerPage);

        setFuncionarioTreinamentos(getFuncionarioTreinamentosUseCase.items);

        pagination.setRowsPerPage(getFuncionarioTreinamentosUseCase.pageSize);
        pagination.setTotalItems(getFuncionarioTreinamentosUseCase.totalItems);
      }
    } catch (e: unknown) {
      setFuncionarioTreinamentos([]);
      throw e;
    }
  }, [
    idFuncionario,
    getFuncionarioTreinamentosUseCase,
    pagination
  ]);

  const loadTreinamentosDropDown = useCallback(async () => {
    try {
      setErrorMessage(undefined);
      const treinamentoLista = await getTreinamentosDropDownUseCase.getTreinamentosDropDown();
      useGestaoTreinamentosCadastroFilters.setState({ treinamentoLista });
    } catch (e: unknown) {
      useGestaoTreinamentosCadastroFilters.setState({ treinamentoLista: [] });
      throw e;
    }
  }, [
    getTreinamentosDropDownUseCase,
  ]);

  const loadFuncionarioAssinaturasDropDown = useCallback(async () => {
    try {
      const funcionarioAssinaturas = await getFuncionarioAssinaturaDropDownUseCase.getFuncionarioAssinaturaDropDown();
      useGestaoTreinamentosCadastroFilters.setState({ funcionarioAssinaturas });
    } catch (e: unknown) {
      useGestaoTreinamentosCadastroFilters.setState({ funcionarioAssinaturas: [] });
      throw e;
    }
  }, [
    getFuncionarioAssinaturaDropDownUseCase,
  ]);

  /**
   * useEffects
   */

  usePageStartBootstrap(async () => {
    try {
      const params = new URLSearchParams(location.search);
      const locationId = params.get('id');
      const id = idFuncionario ?? locationId ?? null;

      if (!id) throw new Error("Id invalido!");

      setErrorMessage(undefined);
      setIdFuncionario(Number(id));

      await loadTreinamentosDropDown();
      await loadFuncionarioAssinaturasDropDown();
      await loadFuncionarioTreinamentos({ id: Number(id) });
      await loadFuncionario({ idFuncionario: Number(id) });
      await loadAfastamentos({ id: Number(id) });
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao iniciar pagina!")[0]);
    }
  });

  /**
   * Eventos
   */

  const handleSubmitTreinamento = useCallback(async (form: TreinamentoForm) => {
    try {
      if (!saving && !loading) {
        setSaving(true);

        if (idFuncionario) {
          if (!selectedFuncionarioTreinamento) {
            await createFuncionarioTreinamentoUseCase.createFuncionarioTreinamento(
              idFuncionario,
              form.idTreinamento,
              form.cargaHoraria,
              form.local,
              form.instrutor,
              form.dataRealização,
              form.prazoAvaliacao,
            );
          } else {
            await updateFuncionarioTreinamentoUseCase.updateFuncionarioTreinamento(
              selectedFuncionarioTreinamento.idFuncionarioTreinamento,
              form.idTreinamento,
              form.cargaHoraria,
              form.local,
              form.instrutor,
              form.dataRealização,
              form.prazoAvaliacao,
            );
          }
          await loadFuncionarioTreinamentos();
          pushInfoNotification("Sucesso", "Registro salvo com sucesso!");
        }
      }
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao salvar documento!"));
    } finally {
      setSaving(false);
      setOpenCadastroFuncionarioTreinamentoModal(false);
    }
  }, [
    saving,
    loading,
    idFuncionario,
    selectedFuncionarioTreinamento,
    createFuncionarioTreinamentoUseCase,
    updateFuncionarioTreinamentoUseCase,
    loadFuncionarioTreinamentos,
    pushErrorNotification,
    pushInfoNotification,
  ]);

  const handleSubmitAvaliarTreinamento = useCallback(async (form: AvaliarTreinamentoForm) => {
    try {
      if (!saving && !loading) {
        setSaving(true);

        if (idFuncionario) {
          if (selectedFuncionarioTreinamento) {
            await avaliarTreinamentoUseCase.avaliarTreinamento(
              selectedFuncionarioTreinamento.idFuncionarioTreinamento,
              form.eficaz,
              form.avaliador,
              form.dataAvaliacao,
              form.evidenciaObjetiva,
              CommonConstants.avaliarTreinamentoScreen.GestaoTreinamentosCadastro,
            );
          } else if (rowsSelectionModel) {
            const idFuncionarioTreinamentosLote: number[] = [];
            for (let row of rowsSelectionModel.ids.values()) {
              if (typeof row === 'number') {
                idFuncionarioTreinamentosLote.push(row);
              } else if (typeof row === 'string') {
                try {
                  idFuncionarioTreinamentosLote.push(Number(row));
                } catch (e) {
                }
              }
            }

            await avaliarTreinamentoUseCase.avaliarTreinamentoLote(
              idFuncionarioTreinamentosLote,
              form.eficaz,
              form.avaliador,
              form.dataAvaliacao,
              form.evidenciaObjetiva,
              CommonConstants.avaliarTreinamentoScreen.GestaoTreinamentosCadastro,
            );
          }
          await loadFuncionarioTreinamentos();
        }
        pushInfoNotification("Sucesso", "Registro salvo com sucesso!");
      }
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao salvar documento!"));
    } finally {
      setSaving(false);
      setOpenAvaliarFuncionarioTreinamentoModal(false);
      setRowsSelectionModel({ type: 'include', ids: new Set() });
    }
  }, [
    saving,
    loading,
    idFuncionario,
    selectedFuncionarioTreinamento,
    rowsSelectionModel,
    avaliarTreinamentoUseCase,
    loadFuncionarioTreinamentos,
    pushErrorNotification,
    pushInfoNotification,
  ]);

  const handleReloadPage = useCallback(async () => {
    try {
      if (!saving && !loading) {
        setLoading(true);

        await loadTreinamentosDropDown();
        await loadFuncionarioTreinamentos();
        await loadFuncionarioTreinamentos({ id: idFuncionario });
        await loadFuncionario({ idFuncionario });
        await loadAfastamentos({ id: idFuncionario });
      }
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar pagina!"));
    } finally {
      setLoading(false);
      setOpenAvaliarFuncionarioTreinamentoModal(false);
    }
  }, [
    loading,
    saving,
    idFuncionario,
    loadTreinamentosDropDown,
    loadFuncionarioTreinamentos,
    loadFuncionario,
    loadAfastamentos,
    pushErrorNotification,
  ]);

  const handleBack = useCallback(() => {
    backOrNavigate('/GestaoTreinamentos_lista');
  }, [
    backOrNavigate,
  ]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      if (!saving && !loading) {
        setLoading(true);
        useGestaoTreinamentosCadastroPagination.getState().setPage(page + 1);
        await loadFuncionarioTreinamentos({ page: page + 1 });
      }
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar pagina!"));
    } finally {
      setLoading(false);
      setOpenAvaliarFuncionarioTreinamentoModal(false);
    }
  }, [
    saving,
    loading,
    loadFuncionarioTreinamentos,
    pushErrorNotification,
  ]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      if (!saving && !loading) {
        setLoading(true);
        useGestaoTreinamentosCadastroPagination.getState().setPage(1);
        useGestaoTreinamentosCadastroPagination.getState().setRowsPerPage(rowsPerPage);
        await loadFuncionarioTreinamentos({ page: 1, rowsPerPage });
      }
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar pagina!"));
    } finally {
      setLoading(false);
      setOpenAvaliarFuncionarioTreinamentoModal(false);
    }
  }, [
    saving,
    loading,
    loadFuncionarioTreinamentos,
    pushErrorNotification,
  ]);

  const handleRowsSelectionChange = useCallback((rowsSelection: GridRowSelectionModel) => {
    setRowsSelectionModel(rowsSelection);
  }, []);

  const handleOpenCadastroFuncionarioTreinamentoModal = useCallback((idFuncionarioTreinamento: number | null, openState: boolean) => {
    if (openState) {
      if (idFuncionarioTreinamento && funcionarioTreinamentos) {
        setSelectedFuncionarioTreinamento(
          funcionarioTreinamentos.find(x => x.idFuncionarioTreinamento === idFuncionarioTreinamento));
      } else {
        setSelectedFuncionarioTreinamento(undefined);
      }
    }
    setOpenCadastroFuncionarioTreinamentoModal(openState);
  }, [
    funcionarioTreinamentos,
  ]);

  const handleOpenAvaliarFuncionarioTreinamentoModal = useCallback((idFuncionarioTreinamento: number | null, openState: boolean) => {
    if (openState) {
      if (idFuncionarioTreinamento && funcionarioTreinamentos) {
        setSelectedFuncionarioTreinamento(
          funcionarioTreinamentos.find(x => x.idFuncionarioTreinamento === idFuncionarioTreinamento));
      } else {
        setSelectedFuncionarioTreinamento(undefined);
      }
    }
    setOpenAvaliarFuncionarioTreinamentoModal(openState);
  }, [
    funcionarioTreinamentos,
  ]);

  const handleOpenDeletarFuncionarioTreinamentoModal = useCallback((idFuncionarioTreinamento: number | null, openState: boolean) => {
    if (openState) {
      if (idFuncionarioTreinamento && funcionarioTreinamentos) {
        setSelectedFuncionarioTreinamento(
          funcionarioTreinamentos.find(x => x.idFuncionarioTreinamento === idFuncionarioTreinamento));
      } else {
        setSelectedFuncionarioTreinamento(undefined);
      }
    }
    setOpenDeletarFuncionarioTreinamentoModal(openState);
  }, [
    funcionarioTreinamentos,
  ]);

  const handleConfirmarDeletarFuncionarioTreinamento = useCallback(async () => {
    if (selectedFuncionarioTreinamento) {
      try {
        setSaving(true);

        await deleteFuncionarioTreinamentoUseCase.deleteFuncionarioTreinamento(
          selectedFuncionarioTreinamento.idFuncionarioTreinamento,
        );

        await loadFuncionarioTreinamentos();
        pushInfoNotification("Sucesso", "Registro excluido com sucesso!");
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao deletar funcionario treinamento!"));
      } finally {
        setSaving(false);
        setSelectedFuncionarioTreinamento(undefined);
        setOpenDeletarFuncionarioTreinamentoModal(false);
      }
    }
  }, [
    selectedFuncionarioTreinamento,
    deleteFuncionarioTreinamentoUseCase,
    loadFuncionarioTreinamentos,
    pushErrorNotification,
    pushInfoNotification,
  ]);

  const handleRelatorioFuncionarioTreinamentoOpenState = useCallback((idAvaliacaoTreinamento: number | null, openState: boolean) => {
    if (openState) {
      if (idAvaliacaoTreinamento && funcionarioTreinamentos) {
        const funcionarioTreinamento = funcionarioTreinamentos.find(x => x.idFuncionarioTreinamento === idAvaliacaoTreinamento);
        setSelectedFuncionarioTreinamento(funcionarioTreinamento);
      } else {
        setSelectedFuncionarioTreinamento(undefined);
      }
    }
    setOpenRelatorioFuncionarioTreinamentoModal(openState);
  }, [funcionarioTreinamentos]);

  const handleSubmitRelatorioFuncionarioTreinamento = useCallback(async (form: RelatorioFuncionarioTreinamentoForm) => {
    try {
      setLoading(true);
      if (selectedFuncionarioTreinamento) {
        await startReportViewerProcess({
          fetchCallback: async (requiredMimeType) =>
            await getReportFuncionarioTreinamentoUseCase.getReportFuncionarioTreinamento(
              requiredMimeType,
              selectedFuncionarioTreinamento.idFuncionarioTreinamento,
              form.exibirConteudoProgramatico,
              form.instrutorAssinatura,
            ),
          closeReportModalCallback: () => {
            setSelectedFuncionarioTreinamento(undefined);
            setRowsSelectionModel(undefined);
            setOpenRelatorioFuncionarioTreinamentoModal(false);
          }
        });
      }
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar relatorio de avaliação treinamento!"));
    } finally {
      setLoading(false);
    }
  }, [
    selectedFuncionarioTreinamento,
    getReportFuncionarioTreinamentoUseCase,
    startReportViewerProcess,
    pushErrorNotification,
  ]);

  const handleAvaliarLoteTreinamentoOpenState = useCallback((openState: boolean) => {
    if (openState) {
      setSelectedFuncionarioTreinamento(undefined);
    } else {
      setRowsSelectionModel(undefined);
    }
    setOpenAvaliarFuncionarioTreinamentoModal(openState);
  }, []);

  const handleOpenDeletarFuncionarioTreinamentoLoteModal = useCallback((openState: boolean) => {
    if (openState) {
      setSelectedFuncionarioTreinamento(undefined);
    } else {
      setRowsSelectionModel(undefined);
    }
    setOpenDeletarFuncionarioTreinamentoLoteModal(openState);
  }, []);

  const handleConfirmarDeletarFuncionarioTreinamentosLote = useCallback(async () => {
    if (idFuncionario && rowsSelectionModel) {
      try {
        setSaving(true);
        const idFuncionarioTreinamentosLote: number[] = [];
        for (let row of rowsSelectionModel.ids.values()) {
          if (typeof row === 'number') {
            idFuncionarioTreinamentosLote.push(row);
          } else if (typeof row === 'string') {
            try {
              idFuncionarioTreinamentosLote.push(Number(row));
            } catch (e) {
            }
          }
        }

        await deleteFuncionarioTreinamentosLoteUseCase.deleteFuncionarioTreinamentosLote(
          idFuncionario,
          idFuncionarioTreinamentosLote,
          CommonConstants.avaliarTreinamentoScreen.GestaoTreinamentosCadastro,
        );

        await loadFuncionarioTreinamentos();
        pushInfoNotification("Sucesso", "Registro excluido com sucesso!");
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao deletar funcionario treinamentos!"));
      } finally {
        setSaving(false);
        setSelectedFuncionarioTreinamento(undefined);
        setRowsSelectionModel(undefined);
        setOpenDeletarFuncionarioTreinamentoLoteModal(false);
      }
    }
  }, [
    idFuncionario,
    rowsSelectionModel,
    deleteFuncionarioTreinamentosLoteUseCase,
    loadFuncionarioTreinamentos,
    pushErrorNotification,
    pushInfoNotification,
  ]);

  return {
    loading,
    saving,
    errorMessage,
    funcionario: funcionario ?? null,
    funcionarioTreinamentos: funcionarioTreinamentos ?? [],
    openCadastroFuncionarioTreinamentoModal,
    openAvaliarFuncionarioTreinamentoModal,
    openDeletarFuncionarioTreinamentoModal,
    openDeletarFuncionarioTreinamentoLoteModal,
    openRelatorioFuncionarioTreinamentoModal,
    selectedFuncionarioTreinamento: selectedFuncionarioTreinamento ?? null,
    rowsSelectionModel,
    filters,
    pagination,
    reportViewerState,
    status,
    handleSubmitTreinamento,
    handleSubmitAvaliarTreinamento,
    handleReloadPage,
    handleBack,
    handlePageChange,
    handleRowsPerPageChange,
    handleRowsSelectionChange,
    handleOpenCadastroFuncionarioTreinamentoModal,
    handleOpenAvaliarFuncionarioTreinamentoModal,
    handleOpenDeletarFuncionarioTreinamentoModal,
    handleConfirmarDeletarFuncionarioTreinamento,
    handleRelatorioFuncionarioTreinamentoOpenState,
    handleSubmitRelatorioFuncionarioTreinamento,
    handleAvaliarLoteTreinamentoOpenState,
    handleOpenDeletarFuncionarioTreinamentoLoteModal,
    handleConfirmarDeletarFuncionarioTreinamentosLote,
  };
}
